import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Link } from 'react-router-dom';
import PrescriptionFiltersHeader from '../Header/PrescriptionFiltersHeader';
import ActionTableButton from '../Buttons/ActionTableButton';
import PrescriptionKPI from '../Header/PrescriptionKPI';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import prescriptionService from '../services/prescriptionService'; // Import the service
import { TextField, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { API_BASE_HEALTH_CENTER_URL, API_HEALTH_CENTER_BY_ID } from '../../../helpers/urlHelper';

// Main component to display a table of medical prescriptions
const PrescriptionTable = ({ prescriptions: propPrescriptions = [], onDeletePrescriptions, onStatusChange }) => {
  
  // Base states for the component
  const [prescriptions, setPrescriptions] = useState([]); // List of processed prescriptions
  const [searchTerm, setSearchTerm] = useState(''); // General search term
  const [rowSelection, setRowSelection] = useState({}); // Control of selected rows
  const [isLoading, setIsLoading] = useState(true); // Loading indicator
  const [globalFilter, setGlobalFilter] = useState(''); // Global filter for MaterialReactTable
  const [activeFilters, setActiveFilters] = useState({}); // Active filters for highlighting
  
  // State to maintain original prescriptions (without transformation)
  const [originalPrescriptions, setOriginalPrescriptions] = useState([]);
  // State to maintain filtered prescriptions in original format
  const [filteredOriginalPrescriptions, setFilteredOriginalPrescriptions] = useState([]);
  
  // State for cell editing
  const [editingCell, setEditingCell] = useState(null); // {rowId, columnId, value}
  const [editValue, setEditValue] = useState('');

  // Transforms API data to the format expected by the table
  const transformApiData = async (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  console.log('Original prescriptions:', apiData.length);
  
  // Obtener IDs únicos de clínicas
  const clinicIds = [...new Set(
    apiData
      .map(p => typeof p.clinic === 'string' ? p.clinic : p.clinic?._id)
      .filter(Boolean)
  )];
  
  // Fetch todos los datos de clínicas en paralelo
  const clinicsData = {};
  if (clinicIds.length > 0) {
    const clinicPromises = clinicIds.map(async (id) => {
      const clinic = await fetchClinicById(id);
      return { id, clinic };
    });
    
    const results = await Promise.all(clinicPromises);
    results.forEach(({ id, clinic }) => {
      if (clinic) {
        clinicsData[id] = clinic;
      }
    });
  }
  
  const transformedData = apiData.map(prescription => {
    // Obtener datos de la clínica
    const clinicId = typeof prescription.clinic === 'string' ? prescription.clinic : prescription.clinic?._id;
    const clinicData = clinicsData[clinicId];
    
    if (!prescription.medicines || !Array.isArray(prescription.medicines) || prescription.medicines.length === 0) {
      console.warn('Prescription without medicines:', prescription._id);
      return [{
        id: prescription._id,
        doctorId: prescription.doctor?._id || '',
        doctorName: prescription.doctor?.fullName || prescription.doctor?.name || 'N/A',
        patientId: prescription.patient?._id || '',
        patientName: prescription.patient?.fullName || prescription.patient?.name || 'N/A',
        clinicId: clinicId || '',
        clinicName: clinicData?.name || 'N/A',
        conditionId: prescription.condition?._id || '',
        conditionName: prescription.condition?.name || 'N/A',
        medicineId: '',
        medicineName: 'N/A',
        dosage: '',
        frequency: '',
        duration: '',
        purpose: '',
        refills: "0",
        instructions: '',
        datePrescribed: prescription.datePrescribed || new Date().toISOString().split('T')[0],
        symptoms: prescription.symptoms || '',
        suggestion: prescription.suggestion || '',
        diseases: prescription.diseases?.map(d => d.name || 'N/A').join(", ") || '',
        diseasesIds: prescription.diseases?.map(d => d._id).join(", ") || '',
        labtests: prescription.labtests?.map(t => t.name || 'N/A').join(", ") || '',
        labtestsIds: prescription.labtests?.map(t => t._id).join(", ") || '',
        isActive: prescription.isActive !== undefined ? prescription.isActive : true
      }];
    }
    
    return prescription.medicines.map((medicine, index) => ({
      id: prescription._id,
      doctorId: prescription.doctor?._id || '',
      doctorName: prescription.doctor?.fullName || prescription.doctor?.name || 'N/A',
      patientId: prescription.patient?._id || '',
      patientName: prescription.patient?.fullName || prescription.patient?.name || 'N/A',
      clinicId: clinicId || '',
      clinicName: clinicData?.name || 'N/A',
      conditionId: prescription.condition?._id || '',
      conditionName: prescription.condition?.name || 'N/A',
      medicineId: medicine._id || medicine.medicineRef || '',
      medicineName: medicine.name || 'N/A',
      dosage: medicine.dosage || '',
      frequency: medicine.frequency || '',
      duration: medicine.duration || '',
      purpose: medicine.purpose || '',
      refills: "0",
      instructions: medicine.purpose || '',
      datePrescribed: prescription.datePrescribed || new Date().toISOString().split('T')[0],
      symptoms: prescription.symptoms || '',
      suggestion: prescription.suggestion || '',
      diseases: prescription.diseases?.map(d => d.name || 'N/A').join(", ") || '',
      diseasesIds: prescription.diseases?.map(d => d._id).join(", ") || '',
      labtests: prescription.labtests?.map(t => t.name || 'N/A').join(", ") || '',
      labtestsIds: prescription.labtests?.map(t => t._id).join(", ") || '',
      isActive: prescription.isActive !== undefined ? prescription.isActive : true,
      originalMedicineIndex: index
    }));
  }).flat();
  
  console.log('Transformed prescriptions:', transformedData.length);
  return transformedData;
};
  
  // Effect to transform and load prescriptions from props
useEffect(() => {
  const loadAndTransformData = async () => {
    if (propPrescriptions.length > 0) {
      setOriginalPrescriptions(propPrescriptions);
      setFilteredOriginalPrescriptions(propPrescriptions);
      
      // Ahora transformApiData es asíncrona
      const transformedData = await transformApiData(propPrescriptions);
      setPrescriptions(transformedData);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setPrescriptions([]);
      setOriginalPrescriptions([]);
      setFilteredOriginalPrescriptions([]);
    }
  };

  loadAndTransformData();
}, [propPrescriptions]);

  // Get selected prescriptions from the rowSelection state
  const getSelectedPrescriptions = () => {
    const selectedRows = Object.keys(rowSelection).filter(key => rowSelection[key]);
    return prescriptions.filter((_, index) => selectedRows.includes(index.toString()));
  };

const fetchClinicById = async (clinicId) => {
  try {
    const response = await fetch(`${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTER_BY_ID}/${clinicId}`);
    if (!response.ok) throw new Error('Error al obtener clínica');
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Error fetching clinic:', err);
    return null;
  }
};

  // Function to filter prescriptions (for PrescriptionFiltersHeader)
  const handleFilterChange = async (filters) => {
    // Store active filters for highlighting
    setActiveFilters(filters);
    
    // Get base data to filter
    const baseData = propPrescriptions.length > 0 ? 
  await transformApiData(propPrescriptions) : [];
    
    const filtered = baseData.filter(prescription => {
      return (
        // Match original fields
        (!filters.id || (prescription.id && prescription.id.toString().toLowerCase().includes(filters.id.toLowerCase()))) &&
        (!filters.doctorId || (prescription.doctorId && prescription.doctorId.toString().toLowerCase().includes(filters.doctorId.toLowerCase()))) &&
        (!filters.patientId || (prescription.patientId && prescription.patientId.toString().toLowerCase().includes(filters.patientId.toLowerCase()))) &&
        (!filters.medicineId || (prescription.medicineId && prescription.medicineId.toString().toLowerCase().includes(filters.medicineId.toLowerCase()))) &&
        (!filters.dosage || (prescription.dosage && prescription.dosage.toString().toLowerCase().includes(filters.dosage.toLowerCase()))) &&
        (!filters.datePrescribed || (prescription.datePrescribed && prescription.datePrescribed.includes(filters.datePrescribed))) &&
        // New fields based on table data
        (!filters.doctorName || (prescription.doctorName && prescription.doctorName.toString().toLowerCase().includes(filters.doctorName.toLowerCase()))) &&
        (!filters.patientName || (prescription.patientName && prescription.patientName.toString().toLowerCase().includes(filters.patientName.toLowerCase()))) &&
        (!filters.medicineName || (prescription.medicineName && prescription.medicineName.toString().toLowerCase().includes(filters.medicineName.toLowerCase()))) &&
        (!filters.clinicName || (prescription.clinicName && prescription.clinicName.toString().toLowerCase().includes(filters.clinicName.toLowerCase()))) &&
        (!filters.healthCenterName || (prescription.healthCenterName && prescription.healthCenterName.toString().toLowerCase().includes(filters.healthCenterName.toLowerCase()))) &&
(!filters.healthCenterId || (prescription.healthCenterId && prescription.healthCenterId.toString().toLowerCase().includes(filters.healthCenterId.toLowerCase()))) &&
        (!filters.conditionName || (prescription.conditionName && prescription.conditionName.toString().toLowerCase().includes(filters.conditionName.toLowerCase()))) &&
        (!filters.frequency || (prescription.frequency && prescription.frequency.toString().toLowerCase().includes(filters.frequency.toLowerCase()))) &&
        (!filters.duration || (prescription.duration && prescription.duration.toString().toLowerCase().includes(filters.duration.toLowerCase()))) &&
        (!filters.symptoms || (prescription.symptoms && prescription.symptoms.toString().toLowerCase().includes(filters.symptoms.toLowerCase()))) &&
        (!filters.suggestion || (prescription.suggestion && prescription.suggestion.toString().toLowerCase().includes(filters.suggestion.toLowerCase()))) &&
        (!filters.diseases || (prescription.diseases && prescription.diseases.toString().toLowerCase().includes(filters.diseases.toLowerCase()))) &&
        (!filters.labtests || (prescription.labtests && prescription.labtests.toString().toLowerCase().includes(filters.labtests.toLowerCase()))) &&
        (!filters.instructions || (prescription.instructions && prescription.instructions.toString().toLowerCase().includes(filters.instructions.toLowerCase()))) &&
        // Filter for active status if provided
        (filters.isActive === undefined || prescription.isActive === filters.isActive)
      );
    });

    setPrescriptions(filtered);
    
    // Important: Update filtered original prescriptions for KPIs
    // Get a unique set of filtered prescription IDs
    const filteredIds = new Set(filtered.map(item => item.id));
    const filteredOriginal = originalPrescriptions.filter(prescription => 
      filteredIds.has(prescription._id)
    );
    setFilteredOriginalPrescriptions(filteredOriginal);
    
    // Reset selection when filters change
    setRowSelection({});
  };

  // Universal search (for PrescriptionFiltersHeader and table filter)
  const handleUniversalSearch = async (term) => {
    setSearchTerm(term);
    setGlobalFilter(term); // Use globalFilter from MaterialReactTable
    
    // If there's a search term, apply a basic filter on prescriptions
    if (term) {
      const termLower = term.toLowerCase();
      const baseData = propPrescriptions.length > 0 ? 
  await transformApiData(propPrescriptions) : [];
      
      const searchFiltered = baseData.filter(prescription => {
        // Search in all text fields
        return Object.entries(prescription).some(([key, value]) => {
          // Only search in string properties
          return typeof value === 'string' && value.toLowerCase().includes(termLower);
        });
      });
      
      setPrescriptions(searchFiltered);
      
      // Update filtered original prescriptions for KPIs
      const filteredIds = new Set(searchFiltered.map(item => item.id));
      const filteredOriginal = originalPrescriptions.filter(prescription => 
        filteredIds.has(prescription._id)
      );
      setFilteredOriginalPrescriptions(filteredOriginal);
    } else {
      // If no term, show all prescriptions
      const transformedData = transformApiData(propPrescriptions);
      setPrescriptions(transformedData);
      setFilteredOriginalPrescriptions(originalPrescriptions);
    }
    
    // Reset selection when search changes
    setRowSelection({});
  };

  // Function to handle changes in the table's global filter
  const handleGlobalFilterChange = (newFilter) => {
    setGlobalFilter(newFilter);
    
    // If there are changes in the global filter through the table, also
    // update KPIs consistently
    if (newFilter !== searchTerm) {
      handleUniversalSearch(newFilter);
    }
  };

  const handleStatusChange = async (prescriptionId, newStatus) => {
  try {
    console.log('Attempting to update prescription:', prescriptionId, 'to status:', newStatus);
    
    // Call the service method to update the status
    const result = await prescriptionService.updatePrescriptionActiveStatus(prescriptionId, newStatus);
    console.log('API response:', result);
    
    // Update local state immediately without page reload
    setPrescriptions(prevPrescriptions => 
      prevPrescriptions.map(prescription => 
        prescription.id === prescriptionId 
          ? { ...prescription, isActive: newStatus }
          : prescription
      )
    );

    // Update original prescriptions state
    setOriginalPrescriptions(prevOriginal => 
      prevOriginal.map(prescription => 
        prescription._id === prescriptionId 
          ? { ...prescription, isActive: newStatus }
          : prescription
      )
    );

    // Update filtered original prescriptions state
    setFilteredOriginalPrescriptions(prevFiltered => 
      prevFiltered.map(prescription => 
        prescription._id === prescriptionId 
          ? { ...prescription, isActive: newStatus }
          : prescription
      )
    );
    
    // Notify parent component if callback is provided
    if (onStatusChange) {
      onStatusChange(prescriptionId, newStatus);
    }
    
    console.log(`Prescription ${prescriptionId} status updated to ${newStatus ? 'active' : 'inactive'}`);
    
    // REMOVIDO: window.location.reload(); - Ya no necesitamos recargar la página
  } catch (error) {
    console.error(`Error updating prescription status:`, error);
    console.error('Error details:', error.response?.data || error.message);
    alert('Error updating prescription status. Please try again.');
  }
};

  // Function to update data after deletion
  const refreshData = (removedItem = null) => {
    if (removedItem) {
      const updatedPrescriptions = prescriptions.filter(p => p.id !== removedItem.id);
      setPrescriptions(updatedPrescriptions);
      
      // Also update original prescriptions
      const updatedOriginal = originalPrescriptions.filter(p => p._id !== removedItem.id);
      setOriginalPrescriptions(updatedOriginal);
      
      // Update filtered prescriptions
      const updatedFilteredOriginal = filteredOriginalPrescriptions.filter(p => p._id !== removedItem.id);
      setFilteredOriginalPrescriptions(updatedFilteredOriginal);
      
      // Notify parent component about deletion
      if (onDeletePrescriptions) {
        onDeletePrescriptions(removedItem);
      }
      
      // Reset selection after deletion
      setRowSelection({});
    }
  };

  // Handler for row click
  const handleRowClick = (row) => {
    console.log('Selected prescription:', row.original);
    // You can add additional logic for row selection here
  };

  // Function to highlight text based on search terms with complete validations
  const highlightText = (text, filterTerm, fieldName) => {
    // Step 1: Verify if the text is undefined or null
    if (text === undefined || text === null) {
      return '';
    }
    
    // Step 2: Convert text to string if it's not
    const textStr = typeof text === 'string' ? text : String(text);
    
    // Step 3: Get the filter term with validations
    let termToHighlight = '';
    if (fieldName && activeFilters && activeFilters[fieldName]) {
      termToHighlight = activeFilters[fieldName].toLowerCase();
    } else if (searchTerm) {
      termToHighlight = searchTerm.toLowerCase();
    }
    
    // Step 4: Check if there's a filter term or global filter
    const hasGlobalFilter = globalFilter !== undefined && globalFilter !== null && globalFilter !== '';
    if (!termToHighlight && !hasGlobalFilter) {
      return textStr;
    }
    
    // Step 5: Use the global filter if there's no specific term
    let term = termToHighlight;
    if (!term && hasGlobalFilter) {
      term = globalFilter.toLowerCase();
    }
    
    // Step 6: Final check before splitting
    if (!term || !textStr || !textStr.toLowerCase().includes(term)) {
      return textStr;
    }
    
    // Step 7: Split the text and highlight matches
    try {
      const parts = textStr.split(new RegExp(`(${term})`, 'gi'));
      
      return (
        <>
          {parts.map((part, index) => {
            if (!part) return null;
            return part.toLowerCase() === term.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part;
          })}
        </>
      );
    } catch (error) {
      console.error("Error highlighting text:", error);
      return textStr; // Return original text in case of error
    }
  };

  // Function to handle double click on a cell to start editing
  const handleCellDoubleClick = (cell, row) => {
    // Check if the cell is editable
    if (isEditableField(cell.column.id)) {
      // Only process if we're not already editing
      if (editingCell === null) {
        setEditingCell({
          rowId: row.index,
          columnId: cell.column.id,
          value: cell.getValue() || ''
        });
        setEditValue(cell.getValue() || '');
      }
    }
  };

  // Function to determine if a field is editable
  const isEditableField = (columnId) => {
    // List of editable fields
    const editableFields = [
      'dosage', 
      'frequency', 
      'duration', 
      'instructions',
      'symptoms',
      'suggestion',
      'purpose'
    ];
    
    return editableFields.includes(columnId);
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Function to save edited cell value to API
  const handleSaveEdit = async () => {
    try {
      if (!editingCell) return;
      
      // Get the current row data
      const row = prescriptions[editingCell.rowId];
      if (!row) return;
      
      // Find the original prescription in originalPrescriptions
      const prescriptionId = row.id;
      const originalPrescription = originalPrescriptions.find(p => p._id === prescriptionId);
      
      if (!originalPrescription) {
        console.error('Original prescription not found');
        return;
      }
      
      // Prepare data for API update based on which field is being edited
      let updateData = {};
      
      if (editingCell.columnId === 'symptoms') {
        // Update symptoms directly on the prescription
        updateData = {
          symptoms: editValue
        };
      } else if (editingCell.columnId === 'suggestion') {
        // Update suggestion directly on the prescription
        updateData = {
          suggestion: editValue
        };
      } else {
        // For medicine-related fields, we need to update the medicine array
        const medicineIndex = row.originalMedicineIndex;
        
        // Create a copy of the prescription's medicine array
        const updatedMedicines = [...originalPrescription.medicines];
        
        // Update the specific field in the specific medicine object
        if (medicineIndex >= 0 && medicineIndex < updatedMedicines.length) {
          updatedMedicines[medicineIndex] = {
            ...updatedMedicines[medicineIndex],
            [editingCell.columnId]: editValue
          };
          
          updateData = {
            medicines: updatedMedicines
          };
        }
      }
      
      // Call API to update prescription
      await prescriptionService.updatePrescription(prescriptionId, updateData);
      
      // Update local state
      setPrescriptions(prevPrescriptions => {
        const newPrescriptions = [...prevPrescriptions];
        newPrescriptions[editingCell.rowId] = {
          ...newPrescriptions[editingCell.rowId],
          [editingCell.columnId]: editValue
        };
        return newPrescriptions;
      });
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      
      console.log(`Updated prescription ${prescriptionId}, field: ${editingCell.columnId}, value: ${editValue}`);
      
      // Optional: Show success message to user
    } catch (error) {
      console.error('Error updating prescription:', error);
      // Optional: Show error message to user
    }
  };

  // Custom cell renderer that handles edit mode
  const renderCell = (cell, row) => {
    const isEditing = 
      editingCell && 
      editingCell.rowId === row.index && 
      editingCell.columnId === cell.column.id;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="standard"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            onClick={(e) => e.stopPropagation()} // Prevent row selection
          />
          <IconButton 
            size="small" 
            color="primary" 
            onClick={(e) => {
              e.stopPropagation();
              handleSaveEdit();
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="secondary" 
            onClick={(e) => {
              e.stopPropagation();
              handleCancelEdit();
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </div>
      );
    }
    
    // Return the normal cell content with highlighting when not editing
    return highlightText(cell.getValue(), 
      cell.column.id in activeFilters ? activeFilters[cell.column.id] : null, 
      cell.column.id);
  };

  // Column definitions for MaterialReactTable with highlighted text
  const columns = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 0,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <ActionTableButton 
            item={row.original}
            refreshData={() => refreshData(row.original)}
          />          
        ),
      },
      {
  id: 'activeStatus',
  header: 'Status',
  size: 100,
  Cell: ({ row }) => (
    <FormControlLabel
      control={
        <Switch
          checked={row.original.isActive}
          onChange={(e) => {
            // Prevent row selection when toggling the switch
            e.stopPropagation();
            // CAMBIO AQUÍ: usar row.original.id directamente
            handleStatusChange(row.original.id, e.target.checked);
          }}
          color="primary"
          size="small"
        />
      }
      label={row.original.isActive ? "Active" : "Inactive"}
      onClick={(e) => e.stopPropagation()} // Prevent row selection
    />
  ),
},
      {
        accessorKey: 'id',
        header: 'Prescription ID',
        size: 150,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'doctorName',
        header: 'Doctor',
        size: 180,
        Cell: ({ cell, row }) => (
          <div>
            <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.doctorId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'patientName',
        header: 'Patient',
        size: 180,
        Cell: ({ cell, row }) => (
          <div>
            <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.patientId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'clinicName',
        header: 'Clinic',
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.clinicId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'conditionName',
        header: 'Condition',
        size: 150,
        Cell: ({ cell, row }) => (
          <div>
            <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.conditionId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'symptoms',
        header: 'Symptoms',
        size: 200,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'suggestion',
        header: 'Suggestion',
        size: 200,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'medicineName',
        header: 'Medicine',
        size: 180,
        Cell: ({ cell, row }) => (
          <div>
            <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
            <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.medicineId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'dosage',
        header: 'Dosage',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'frequency',
        header: 'Frequency',
        size: 150,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        size: 120,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'purpose',
        header: 'Purpose',
        size: 150,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'diseases',
        header: 'Diseases',
        size: 200,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'labtests',
        header: 'Lab Tests',
        size: 200,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
      {
        accessorKey: 'datePrescribed',
        header: 'Date',
        size: 150,
        Cell: ({ cell, row }) => renderCell(cell, row),
      },
    ],
[activeFilters, searchTerm, globalFilter, editingCell, editValue, handleStatusChange, refreshData, renderCell],  );

  return (
    <div className="container-fluid px-lg-5">
      {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Prescriptions</li>
                            </ol>
                        </nav>
      {/* KPI component with original and filtered data */}
      <PrescriptionKPI 
        prescriptions={originalPrescriptions} 
        filteredPrescriptions={filteredOriginalPrescriptions} 
      />
      
      {/* Header component with table filters */}
      <PrescriptionFiltersHeader 
        onFilterChange={handleFilterChange} 
        onSearchChange={handleUniversalSearch}
        selectedPrescriptions={getSelectedPrescriptions()}
        onClearSelection={() => setRowSelection({})}
        onBulkDelete={(prescriptionsToDelete) => {
          // Remove selected prescriptions from local state
          const remainingPrescriptions = prescriptions.filter(
            prescription => !prescriptionsToDelete.some(p => p.id === prescription.id)
          );
          
          setPrescriptions(remainingPrescriptions);
          
          // Also update original prescriptions
          const deletedIds = new Set(prescriptionsToDelete.map(p => p.id));
          const updatedOriginal = originalPrescriptions.filter(p => !deletedIds.has(p._id));
          setOriginalPrescriptions(updatedOriginal);
          
          // Update filtered prescriptions
          const updatedFilteredOriginal = filteredOriginalPrescriptions.filter(p => !deletedIds.has(p._id));
          setFilteredOriginalPrescriptions(updatedFilteredOriginal);
          
          // Notify parent component about bulk deletions - pass the full array
          if (onDeletePrescriptions && prescriptionsToDelete.length > 0) {
            onDeletePrescriptions(prescriptionsToDelete); // Pass the complete array
          }
          
          setRowSelection({});
        }}
      />

      {/* Main table with MaterialReactTable */}
      <MaterialReactTable
        columns={columns}
        data={prescriptions}
        state={{ 
          isLoading,
          rowSelection,
          globalFilter
        }}
        enableRowSelection
        onRowSelectionChange={setRowSelection}
        enableColumnFilters
        enableColumnOrdering
        enableSorting
        enablePagination
        enableGlobalFilter
        onGlobalFilterChange={handleGlobalFilterChange} // New handler for global filter
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => handleRowClick(row),
          onDoubleClick: (event) => {
            // Find which cell was double-clicked
            const cellElement = event.target.closest('td');
            if (cellElement) {
              const cellIndex = Array.from(cellElement.parentNode.children).indexOf(cellElement);
              // Map cell index to column
              const column = columns[cellIndex];
              if (column) {
                handleCellDoubleClick(
                  { column, getValue: () => row.original[column.accessorKey] }, 
                  row
                );
              }
            }
          },
          sx: { 
            cursor: 'pointer',
            backgroundColor: row.original.isActive ? 'rgba(144, 238, 144, 0.13)' : 'rgba(255, 182, 193, 0.2)',
          },
        })}
        muiTableBodyCellProps={({ cell }) => ({
          onDoubleClick: (event) => {
            event.stopPropagation();
            handleCellDoubleClick(cell, cell.row);
          },
          // Add visual indication for editable cells
          sx: isEditableField(cell.column.id) ? {
            position: 'relative',
            '&:hover::after': {
              content: '"✎"',
              position: 'absolute',
              top: '2px',
              right: '2px',
              fontSize: '10px',
              color: '#666',
            }
          } : {}
        })}
        initialState={{
          density: 'compact',
          pagination: { pageIndex: 0, pageSize: 10 }
        }}
        // Handle filtering in each column to also update KPIs
        muiTableHeadCellFilterTextFieldProps={{
          onChange: (e) => {
            // When filtering by column, we also update the KPIs
            // using our custom filtering system
            const fieldName = e.target.name || '';
            const value = e.target.value || '';
            // Create a filter object with this field
            const newFilters = { ...activeFilters, [fieldName]: value };
            
            // If value is empty, remove the filter
            if (!value) {
              delete newFilters[fieldName];
            }
            
            // Apply the new filters
            handleFilterChange(newFilters);
          }
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            border: '1px solid #e0e0e0',
            overflow: 'hidden'
          }
        }}
        muiTableProps={{
          sx: {
            '& .MuiTableCell-head': {
              fontWeight: 'bold',
              borderBottom: '2px solid #e0e0e0'
            }
          }
        }}
      />
    </div>
  );
};

export default PrescriptionTable;